<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, between, numeric } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'

const props = defineProps<{
  disable?: boolean
}>()

const { t } = useI18n()
const { umsokn } = useUmsokn()

// --- Date helpers ---
const deathDateMenu = ref(false) // naming consistent with step2 pattern, just a menu toggle
const startOfYearISO = computed(() => {
  const d = new Date()
  d.setMonth(0, 1)
  d.setHours(0, 0, 0, 0)
  return d.toISOString().split('T')[0]
})
const todayISO = () => new Date().toISOString().split('T')[0]

// --- Vuelidate rules (same style as your other steps) ---
const rules = {
  umsokn: {
    taxStep: { required },
    taxDiscount: { numeric, between: between(0, 100) },
    fromDate: { required }
  }
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="form__header">
          {{ t('barnalifeyrir_umsokn_step4_header') }}
        </h2>
      </v-col>
    </v-row>

    <!-- Tax step options -->
    <v-row>
      <v-col>
        <v-radio-group
          v-model="umsokn.taxStep"
          :readonly="props.disable"
          :error="v$.umsokn.taxStep.$error"
          hide-details
          :error-messages="v$.umsokn.taxStep.$error ? [t('barnalifeyrir_umsokn_step4_taxstep_error')] : []"
        >
          <v-radio :label="t('barnalifeyrir_umsokn_step4_taxstep1')" :value="1" />
          <v-radio :label="t('barnalifeyrir_umsokn_step4_taxstep2')" :value="2" />
          <v-radio :label="t('barnalifeyrir_umsokn_step4_taxstep3')" :value="3" />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- Person tax reduction + From date -->
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.taxDiscount"
          type="number"
          :readonly="props.disable"
          :label="t('barnalifeyrir_umsokn_step4_persontaxreduction')"
          variant="outlined"
          density="comfortable"
          :error="v$.umsokn.taxDiscount.$error"
          :error-messages="[
            !v$.umsokn.taxDiscount.numeric ? t('barnalifeyrir_umsokn_step4_persontaxreduction_numeric') : '',
            !v$.umsokn.taxDiscount.between ? t('barnalifeyrir_umsokn_step4_persontaxreduction_between') : ''
          ].filter(Boolean)"
        />
      </v-col>

      <v-col cols="12" md="6">
        <DatePicker
          v-model="umsokn.fromDate"
          :label="t('lifeyri_umsokn_step4_datefrom')"
          :disabled="props.disable"
          :min="startOfYearISO"
          :max="todayISO()"
          :error="v$.umsokn.fromDate.$error"
          :error-messages="v$.umsokn.fromDate.$error ? [t('lifeyri_umsokn_step4_datefrom')] : []"
        />
      </v-col>
    </v-row>

    <!-- Extra info -->
    <v-row>
      <v-col cols="12">
        <p class="forms__paragraph">
          {{ t('barnalifeyrir_umsokn_step4_aukaupplysingar') }}
        </p>
      </v-col>
      <v-col cols="12">
        <p>{{ t('barnalifeyrir_umsokn_step4_aukaupplysingar2') }}</p>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
/* keep styles minimal; follows your existing conventions */
</style>
